// Turns an RTK Query / fetch / Office.js failure into something a person can act on.
//
// Deliberately NOT the raw error. "FETCH_ERROR: TypeError: Failed to fetch" is a
// faithful description of what happened and useless to the person reading it — it
// names no cause they recognise and suggests nothing they can do. Every branch here
// answers "what went wrong, and what now?".
//
// Nothing is lost: the original is logged, so a bug report still has the detail.

import { FINISH_THE_EDIT, isDocumentUnavailable } from "../platform/shared/hostReadErrors";

interface RtkError {
  status?: number | string;
  data?: unknown;
  error?: string;
}

const GENERIC = "Something went wrong. Please try again.";
const OFFLINE = "Could not reach Jylo. Check your connection and try again.";
const SLOW = "That took too long to respond. Please try again.";

const forStatus = (status: number | string): string => {
  if (typeof status === "number") {
    if (status === 401) return "Your session has expired. Sign in again to continue.";
    if (status === 403) return "You do not have permission to do that.";
    if (status === 404) return "Jylo could not find that — it may have been moved or deleted.";
    if (status === 408) return SLOW;
    if (status === 409) return "Someone else changed this first. Reopen it and try again.";
    if (status === 413) return "This document is too large to send.";
    if (status === 429) return "Too many requests at once. Wait a moment and try again.";
    if (status >= 500) return "Jylo had a problem completing that. Please try again shortly.";
    return GENERIC;
  }

  switch (status) {
    case "FETCH_ERROR":
      return OFFLINE;
    case "TIMEOUT_ERROR":
      return SLOW;
    case "PARSING_ERROR":
      return "Jylo returned an unexpected response.";
    default:
      return GENERIC;
  }
};

const looksLikeNetworkFailure = (message: string): boolean =>
  /failed to fetch|networkerror|network request failed|load failed/i.test(message);

export const describeError = (error: unknown): string => {
  // The user gets plain language; the console keeps what a bug report needs.
  console.warn("[Jylo] error", error);

  if (error && typeof error === "object") {
    const rtk = error as RtkError;

    if (rtk.status !== undefined) {
      return forStatus(rtk.status);
    }

    if (error instanceof Error) {
      // Messages we author are already written for a person; the ones the platform
      // throws are not, and "Failed to fetch" is the one that actually reaches users.
      if (looksLikeNetworkFailure(error.message)) return OFFLINE;
      // "Invalid API call in the current context" is the other one that reaches them,
      // and it is Office refusing to hand over a document that is still being edited.
      if (isDocumentUnavailable(error)) return FINISH_THE_EDIT;
      return error.message || GENERIC;
    }

    return GENERIC;
  }

  return typeof error === "string" && error.trim() ? error : GENERIC;
};
